import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Send as TestIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  PositionType,
  SingleTriggerPositionType,
  AlertConfiguration as AlertConfigType,
  AlertKind,
  TriggerStatusResponse,
} from '../../types/dashboard';
import { apiService, ApiError } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import CustomButton from '../ui/Button';
import AlertStatus from '../ui/AlertStatus';
import MultipleWebhooksManager from './MultipleWebhooksManager';
import ReauthDialog from '../common/ReauthDialog';

const DIRECTIONAL_POSITION_TYPES = ['Breakout', 'Reversal', 'Sideways/Ranging', 'Other'] as const;

const SINGLE_TRIGGER_POSITION_TYPES: readonly SingleTriggerPositionType[] = [
  'Up',
  'Down',
  'Long',
  'Short',
  'Call',
  'Put',
  'Sideways',
  'Other',
  'Out',
];

function normalizeSinglePositionType(raw: string): SingleTriggerPositionType {
  const u = (raw ?? '').trim();
  const hit = SINGLE_TRIGGER_POSITION_TYPES.find((x) => x.toLowerCase() === u.toLowerCase());
  if (hit) return hit;
  return 'Other';
}

const LEGACY_MULTI_POSITION_TYPES: PositionType[] = [
  'Custom',
  'Call',
  'Put',
  'Short Call Spread',
  'Short Put Spread',
  'Long Call Spread',
  'Long Put Spread',
  'Calendar',
  'Diagonal',
  'Straddle',
  'Strangle',
  'Collar',
  'Butterfly',
  'Condor',
  'Iron Butterfly',
  'Iron Condor',
];

export /** Per-slot incoming webhook credentials from API (BMTA uses tokenUp + tokenDown). */
type SlotIncomingCreds = {
  token?: string | null;
  tokenUp?: string | null;
  tokenDown?: string | null;
} | null;

/** FastAPI often returns snake_case; accept camelCase too. */
function slotIncomingCredsFromApiPayload(d: unknown): SlotIncomingCreds {
  if (!d || typeof d !== 'object') return null;
  const o = d as Record<string, unknown>;
  const pick = (a: string, b: string): string | null | undefined => {
    const v = o[a] ?? o[b];
    if (v == null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  };
  const rawToken = o.token;
  const token =
    rawToken == null
      ? null
      : (() => {
          const s = String(rawToken).trim();
          return s.length ? s : null;
        })();
  const tokenUp = pick('tokenUp', 'token_up');
  const tokenDown = pick('tokenDown', 'token_down');
  if (token || tokenUp || tokenDown) {
    return { token: token ?? null, tokenUp: tokenUp ?? null, tokenDown: tokenDown ?? null };
  }
  return null;
}

function initialSlotDescriptions(alert: AlertConfigType | undefined, triggerCount: number): string[] {
  const tc = Math.min(Math.max(triggerCount, 1), 7);
  const a = alert as AlertConfigType & { slotDescriptions?: string[] };
  const raw = alert?.slot_descriptions ?? a?.slotDescriptions;
  const out = Array.from({ length: tc }, () => '');
  if (Array.isArray(raw)) {
    for (let i = 0; i < Math.min(raw.length, tc); i++) {
      out[i] = String(raw[i] ?? '');
    }
  }
  return out;
}

interface AlertConfigurationProps {
  /** directional_multi (1+ slots) or bidirectional_multi */
  alertKind: AlertKind;
  alert?: AlertConfigType;
  onSave: (
    alert: Omit<AlertConfigType, 'id'> & { id?: string },
    options?: { stayOnForm?: boolean }
  ) => void;
  onCancel: () => void;
  onTestWebhook: (webhookUrl: string) => Promise<{ success: boolean; executionTime?: number }>;
  loading?: boolean;
}

const AlertConfiguration: React.FC<AlertConfigurationProps> = ({
  alertKind,
  alert,
  onSave,
  onCancel,
  onTestWebhook,
  loading = false,
}) => {
  const { token } = useAuth();
  const isMultiKind = alertKind === 'directional_multi' || alertKind === 'bidirectional_multi';
  const isDirectionalMulti = alertKind === 'directional_multi';
  const isBidirectionalMulti = alertKind === 'bidirectional_multi';
  /** Legacy multi-trigger rows (no alert_kind) used classic option position types in the Position Type field */
  const isLegacyMultiRow =
    !!alert?.id && !alert?.alert_kind && !!(alert as AlertConfigType).is_multi_trigger;
  const useClassicPositionOptions = isLegacyMultiRow;
  const [maxTriggerCount, setMaxTriggerCount] = useState<number>(7);
  const mapPositionToDirectional = (raw: string): string => {
    const key = (raw ?? '').trim().toLowerCase();
    const legacyMap: Record<string, (typeof DIRECTIONAL_POSITION_TYPES)[number]> = {
      up: 'Breakout',
      down: 'Reversal',
      sideways: 'Sideways/Ranging',
      other: 'Other',
      breakout: 'Breakout',
      reversal: 'Reversal',
      'sideways/ranging': 'Sideways/Ranging',
    };
    if (legacyMap[key]) return legacyMap[key];
    const u = normalizeSinglePositionType(raw);
    if (DIRECTIONAL_POSITION_TYPES.includes(u as (typeof DIRECTIONAL_POSITION_TYPES)[number])) return u;
    return 'Other';
  };
  const [formData, setFormData] = useState({
    stockSymbol: alert?.stock_symbol || alert?.stockSymbol || '',
    alertName: alert?.description || '',
    positionType: mapPositionToDirectional(
      String(alert?.position_type || alert?.positionType || 'Breakout'),
    ),
    customPositionType: '', // For custom position type input (legacy multi only)
    secretWord: alert?.secret_word || alert?.secretWord || '',
    webhookUrl:
      alertKind === 'bidirectional_multi'
        ? ''
        : alert?.webhook_url || alert?.webhookUrl || '',
    customPayload: '', // Optional; applied to the first webhook on create
    status: alert?.is_active ? 'active' : (alert?.status || 'active'),
    isMultiTrigger: !!alert?.is_multi_trigger,
    triggerCount: Math.min(Math.max(alert?.trigger_count ?? 1, 1), 7),
    slotSources: (alert?.slot_sources?.length ? alert.slot_sources : ['email']) as string[],
    slotDescriptions: initialSlotDescriptions(alert, Math.min(Math.max(alert?.trigger_count ?? 1, 1), 7)),
    linkedDirectionalParentId:
      alert?.linked_directional_alert_id != null ? String(alert.linked_directional_alert_id) : '',
    linkedDirectionalSlotIndex:
      alert?.linked_directional_slot_index != null ? String(alert.linked_directional_slot_index) : '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; executionTime?: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const [webhookManagerContext, setWebhookManagerContext] = useState<{
    alertId: number;
    alertName: string;
  } | null>(null);
  /** Bump after closing Manage Webhooks so custom payload reloads from primary webhook. */
  const [primaryWebhookMetaNonce, setPrimaryWebhookMetaNonce] = useState(0);
  const [primaryWebhookId, setPrimaryWebhookId] = useState<number | null>(null);
  const [payloadPresent, setPayloadPresent] = useState(false);
  const [payloadRevealed, setPayloadRevealed] = useState(false);
  const [payloadDirty, setPayloadDirty] = useState(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [triggerToken, setTriggerToken] = useState<string | null>(null);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerGenerating, setTriggerGenerating] = useState(false);
  const [slotTriggerTokens, setSlotTriggerTokens] = useState<Record<number, SlotIncomingCreds>>({});
  const [slotTokenLoading, setSlotTokenLoading] = useState<Record<number, boolean>>({});
  const [slotTokenGenerating, setSlotTokenGenerating] = useState<Record<number, boolean>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'error'
  });
  const [savingContinue, setSavingContinue] = useState(false);
  const [peerConfigs, setPeerConfigs] = useState<AlertConfigType[]>([]);
  const [bmtaTriggerStatus, setBmtaTriggerStatus] = useState<TriggerStatusResponse | null>(null);
  const [bmtaTriggerLoading, setBmtaTriggerLoading] = useState(false);
  const [dmtaTriggerStatus, setDmtaTriggerStatus] = useState<TriggerStatusResponse | null>(null);
  const [dmtaTriggerLoading, setDmtaTriggerLoading] = useState(false);
  const [dmtaStatusNonce, setDmtaStatusNonce] = useState(0);
  const [bmtaResetting, setBmtaResetting] = useState<number | 'all' | null>(null);
  const [bmtaStatusNonce, setBmtaStatusNonce] = useState(0);

  // Load subscription to get max_trigger_count for tier
  useEffect(() => {
    if (!token) return;
    apiService.getSubscription(token).then((res) => {
      if (res.success && res.data?.max_trigger_count != null) {
        const n = Number(res.data.max_trigger_count);
        if (n >= 1) setMaxTriggerCount(Math.min(Math.floor(n), 10));
      }
    }).catch(() => {});
  }, [token]);

  // New alert: when switching create flow (single vs directional vs bi), reset multi fields and position type family
  useEffect(() => {
    if (alert?.id) return;
    const multi = isMultiKind;
    setFormData((prev) => {
      const tc = multi ? Math.max(1, Math.min(prev.triggerCount || 1, maxTriggerCount)) : 1;
      const sources = multi
        ? Array.from({ length: tc }, (_, i) => prev.slotSources?.[i] ?? 'email')
        : ['email'];
      const slotDescriptions = multi
        ? Array.from({ length: tc }, (_, i) => prev.slotDescriptions?.[i] ?? '')
        : [''];
      let positionType = prev.positionType;
      if (multi) {
        if (!DIRECTIONAL_POSITION_TYPES.includes(positionType as (typeof DIRECTIONAL_POSITION_TYPES)[number])) {
          positionType = 'Up';
        }
      } else if (DIRECTIONAL_POSITION_TYPES.includes(positionType as (typeof DIRECTIONAL_POSITION_TYPES)[number])) {
        positionType = 'Call';
      }
      return {
        ...prev,
        isMultiTrigger: multi,
        triggerCount: multi ? tc : 1,
        slotSources: sources,
        slotDescriptions,
        positionType,
        webhookUrl: multi ? '' : prev.webhookUrl,
      };
    });
  }, [alertKind, alert?.id, maxTriggerCount, isMultiKind]);

  // Multi-trigger execution URLs live only under Manage Webhooks — do not mirror alert.webhook_url in this form.
  useEffect(() => {
    if (!isMultiKind) return;
    setFormData((prev) => ({ ...prev, webhookUrl: '' }));
  }, [isMultiKind, alert?.id]);

  const alertSyncIsMultiTrigger = (alert as AlertConfigType & { isMultiTrigger?: boolean })
    ?.isMultiTrigger;
  const alertSyncTriggerCount = (alert as AlertConfigType & { triggerCount?: number })?.triggerCount;
  const alertSyncSlotSources = (alert as AlertConfigType & { slotSources?: string[] })?.slotSources;
  const alertSyncSlotDescriptions = (alert as AlertConfigType & { slotDescriptions?: string[] })
    ?.slotDescriptions;
  const alertSinglePositionTypeCamel = (alert as AlertConfigType & { positionType?: string })
    ?.positionType;

  // When editing, sync multi-trigger fields from alert (snake_case or camelCase API)
  useEffect(() => {
    if (!alert) return;
    const a = alert as AlertConfigType & {
      isMultiTrigger?: boolean;
      triggerCount?: number;
      slotSources?: string[];
      slotDescriptions?: string[];
    };
    const isMt = isMultiKind;
    const rawSlots = a.slot_sources ?? a.slotSources ?? [];
    const rawTc = a.trigger_count ?? a.triggerCount;
    const rawDesc = a.slot_descriptions ?? a.slotDescriptions;
    setFormData((prev) => {
      const inferredTc =
        rawTc ??
        (rawSlots.length > 0 ? rawSlots.length : prev.isMultiTrigger ? prev.triggerCount : 1);
      const tc = Math.min(Math.max(inferredTc ?? 1, 1), maxTriggerCount);
      let slotSources: string[];
      if (!isMt) {
        slotSources = ['email'];
      } else if (rawSlots.length === tc) {
        slotSources = [...rawSlots];
      } else if (rawSlots.length > tc) {
        slotSources = rawSlots.slice(0, tc);
      } else if (rawSlots.length > 0) {
        slotSources = [
          ...rawSlots,
          ...Array.from({ length: tc - rawSlots.length }, () => 'email' as const),
        ];
      } else {
        slotSources = Array.from({ length: tc }, (_, i) => prev.slotSources?.[i] ?? 'email');
      }
      let slotDescriptions: string[];
      if (!isMt) {
        slotDescriptions = [''];
      } else if (Array.isArray(rawDesc) && rawDesc.length === tc) {
        slotDescriptions = rawDesc.map((x) => String(x ?? ''));
      } else {
        slotDescriptions = Array.from({ length: tc }, (_, i) => prev.slotDescriptions?.[i] ?? '');
      }
      return {
        ...prev,
        alertName: alert?.description ?? prev.alertName,
        isMultiTrigger: isMt,
        triggerCount: isMt ? tc : Math.min(1, maxTriggerCount),
        slotSources,
        slotDescriptions,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when listed fields change; `alert` identity is unstable from parent
  }, [
    alert?.id,
    alert?.is_multi_trigger,
    alertSyncIsMultiTrigger,
    alert?.trigger_count,
    alertSyncTriggerCount,
    alert?.slot_sources,
    alertSyncSlotSources,
    alert?.slot_descriptions,
    alertSyncSlotDescriptions,
    maxTriggerCount,
    alertKind,
  ]);

  // Single-trigger (non-legacy): normalize position + Out link fields when opening an alert
  useEffect(() => {
    if (!alert?.id || alertKind !== 'single' || isLegacyMultiRow) return;
    const pt = normalizeSinglePositionType(String(alert.position_type || alert.positionType || ''));
    const lid = alert.linked_directional_alert_id;
    const ls = alert.linked_directional_slot_index;
    setFormData((prev) => ({
      ...prev,
      positionType: pt,
      linkedDirectionalParentId: lid != null ? String(lid) : '',
      linkedDirectionalSlotIndex: ls != null ? String(ls) : '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- narrow deps for single-trigger Out fields; `alert` identity is unstable
  }, [
    alert?.id,
    alertKind,
    isLegacyMultiRow,
    alert?.position_type,
    alertSinglePositionTypeCamel,
    alert?.linked_directional_alert_id,
    alert?.linked_directional_slot_index,
  ]);

  // Peer configs: ambiguity warning for multiple Out alerts (same symbol + secret)
  useEffect(() => {
    if (!token) return;
    if (alertKind !== 'single') {
      setPeerConfigs([]);
      return;
    }
    void apiService.getAlertConfigurations(token).then((r) => {
      if (r.success && r.data) setPeerConfigs(r.data);
    });
  }, [token, alertKind]);

  // Custom payload is stored on the primary AlertWebhook, not on AlertConfiguration — load when editing.
  useEffect(() => {
    if (!token) return;
    if (alertKind !== 'single' && alertKind !== 'directional_multi' && alertKind !== 'bidirectional_multi') return;
    if (!alert?.id) return;
    const rawId = alert.id;
    const aid = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
    if (Number.isNaN(aid)) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiService.getAlertWebhooks(aid, token);
        if (cancelled) return;
        if (!res.success || !res.data?.length) {
          return;
        }
        const rows = res.data as Array<{
          id?: number;
          is_primary?: boolean;
          custom_payload?: string | null;
          customPayload?: string | null;
          payload_present?: boolean;
          payload_masked?: boolean;
        }>;
        const primary = rows.find((w) => w.is_primary) ?? rows[0];
        setPrimaryWebhookId(primary?.id ?? null);
        const present = Boolean(primary?.payload_present);
        setPayloadPresent(present);
        setPayloadRevealed(false);
        setPayloadDirty(false);
        // Payload is masked in API responses — leave blank until re-auth reveal.
        setFormData((prev) => ({ ...prev, customPayload: '' }));
      } catch {
        /* keep existing draft */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, alert?.id, alertKind, primaryWebhookMetaNonce]);

  const refreshBmtaTriggerStatus = useCallback(async () => {
    if (!token || !isBidirectionalMulti || !alert?.id) {
      setBmtaTriggerStatus(null);
      return;
    }
    const rawId = alert.id;
    const aid = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
    if (Number.isNaN(aid)) return;
    setBmtaTriggerLoading(true);
    try {
      const r = await apiService.getTriggerStatus(aid, token);
      if (r.success && r.data) setBmtaTriggerStatus(r.data);
      else setBmtaTriggerStatus(null);
    } catch {
      setBmtaTriggerStatus(null);
    } finally {
      setBmtaTriggerLoading(false);
    }
  }, [token, isBidirectionalMulti, alert?.id]);

  useEffect(() => {
    void refreshBmtaTriggerStatus();
  }, [refreshBmtaTriggerStatus, bmtaStatusNonce]);

  const refreshDmtaTriggerStatus = useCallback(async () => {
    if (!token || !isDirectionalMulti || !alert?.id) {
      setDmtaTriggerStatus(null);
      return;
    }
    const rawId = alert.id;
    const aid = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
    if (Number.isNaN(aid)) return;
    setDmtaTriggerLoading(true);
    try {
      const r = await apiService.getTriggerStatus(aid, token);
      if (r.success && r.data) setDmtaTriggerStatus(r.data);
      else setDmtaTriggerStatus(null);
    } catch {
      setDmtaTriggerStatus(null);
    } finally {
      setDmtaTriggerLoading(false);
    }
  }, [token, isDirectionalMulti, alert?.id]);

  useEffect(() => {
    void refreshDmtaTriggerStatus();
  }, [refreshDmtaTriggerStatus, dmtaStatusNonce]);

  // Easy to read/type: letters that don't look like digits (no O/0, I/1, L/1, Z/2, S/5, B/8, G/6).
  // Digits that don't look like letters: 3, 4, 6, 7, 9 only. 50 words, 3–5 letters.
  const generateSecretWord = () => {
    const words = [
      'ACE', 'ARC', 'CAP', 'CAR', 'CAT', 'DEN', 'ERA', 'HAT', 'JET', 'KEY',
      'MAP', 'MAT', 'NET', 'PAN', 'RED', 'RUN', 'VAN', 'WAR', 'WAX', 'JADE',
      'CAFE', 'QUAD', 'WAVE', 'HAWK', 'DUKE', 'MANE', 'REEF', 'NEXT', 'CREW', 'TREK',
      'PEAR', 'DEAR', 'NEAR', 'CARE', 'DARK', 'DECK', 'DUNE', 'FERN', 'HARE', 'MARE',
      'PARK', 'PAVE', 'RAVE', 'TAME', 'WADE', 'WARM', 'CRANE', 'DANCE', 'HAVEN', 'RAVEN'
    ];
    const safeDigits = ['3', '4', '6', '7', '9'];
    const word = words[Math.floor(Math.random() * words.length)];
    const numPart = Array.from({ length: 3 }, () => safeDigits[Math.floor(Math.random() * safeDigits.length)]).join('');
    return `${word}${numPart}`;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleTriggerCountChange = (value: number) => {
    const count = Math.min(Math.max(value, 1), maxTriggerCount);
    setFormData((prev) => {
      const prevLen = prev.slotSources?.length ?? 0;
      const sources =
        count >= prevLen
          ? [...(prev.slotSources ?? []), ...Array.from({ length: count - prevLen }, () => 'email')]
          : (prev.slotSources ?? []).slice(0, count);
      const nextDesc =
        count >= prevLen
          ? [...(prev.slotDescriptions ?? []), ...Array.from({ length: count - prevLen }, () => '')]
          : (prev.slotDescriptions ?? []).slice(0, count);
      return { ...prev, triggerCount: count, slotSources: sources, slotDescriptions: nextDesc };
    });
    if (errors.slotSources) setErrors((prev) => ({ ...prev, slotSources: '' }));
  };

  const handleSlotSourceChange = (index: number, source: string) => {
    setFormData((prev) => {
      const next = [...(prev.slotSources ?? [])];
      next[index] = source;
      return { ...prev, slotSources: next };
    });
    if (errors.slotSources) setErrors((prev) => ({ ...prev, slotSources: '' }));
  };

  const handleSlotDescriptionChange = (index: number, value: string) => {
    setFormData((prev) => {
      const next = [...(prev.slotDescriptions ?? [])];
      next[index] = value;
      return { ...prev, slotDescriptions: next };
    });
  };

  const triggerCountOptions = Array.from({ length: maxTriggerCount }, (_, i) => i + 1);
  const slotSourceOptions = [
    { value: 'email', label: 'Email' },
    { value: 'webhook', label: 'Webhook' },
  ];

const CUSTOM_PAYLOAD_MAX_BYTES = 8192;

  const ambiguousOutCount = useMemo(() => {
    if (!formData.stockSymbol.trim() || !formData.secretWord.trim()) return 0;
    const sym = formData.stockSymbol.trim().toUpperCase();
    const sec = formData.secretWord.trim();
    return peerConfigs.filter(
      (c) =>
        !c.is_multi_trigger &&
        (c.position_type || '').toLowerCase() === 'out' &&
        (c.stock_symbol || '').toUpperCase() === sym &&
        (c.secret_word || c.secretWord || '') === sec &&
        c.is_active !== false
    ).length;
  }, [peerConfigs, formData.stockSymbol, formData.secretWord]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.stockSymbol.trim()) {
      newErrors.stockSymbol = 'Stock symbol is required';
    } else if (!/^[A-Z]{1,5}$/.test(formData.stockSymbol.toUpperCase())) {
      newErrors.stockSymbol = 'Invalid stock symbol format';
    }

    if (!formData.secretWord.trim()) {
      newErrors.secretWord = 'Secret word is required';
    }

    // Validate custom position type (legacy multi rows only)
    if (useClassicPositionOptions && formData.positionType === 'Custom' && !formData.customPositionType.trim()) {
      newErrors.customPositionType = 'Custom position type description is required';
    }

    const webhookSkippableForMulti = isMultiKind;
    if (!webhookSkippableForMulti) {
      if (!formData.webhookUrl.trim()) {
        newErrors.webhookUrl = 'Webhook URL is required';
      } else if (!/^https?:\/\/.+/.test(formData.webhookUrl)) {
        newErrors.webhookUrl = 'Invalid webhook URL format';
      }
    } else if (formData.webhookUrl.trim() && !/^https?:\/\/.+/.test(formData.webhookUrl)) {
      newErrors.webhookUrl = 'Invalid webhook URL format';
    }

    // Custom payload: max 8KB and valid JSON if provided
    const payload = formData.customPayload?.trim() ?? '';
    if (payload.length > 0) {
      const byteCount = new TextEncoder().encode(payload).length;
      if (byteCount > CUSTOM_PAYLOAD_MAX_BYTES) {
        newErrors.customPayload = `Custom payload must be at most 8KB (${CUSTOM_PAYLOAD_MAX_BYTES} bytes).`;
      } else {
        try {
          JSON.parse(payload);
        } catch {
          newErrors.customPayload = 'Custom payload must be valid JSON when provided.';
        }
      }
    }

    if (isMultiKind) {
      // 'ocr' kept as a tolerated legacy value so existing OCR slots stay editable.
      const allowed = ['email', 'ocr', 'webhook'];
      const sources = formData.slotSources ?? [];
      if (sources.length !== formData.triggerCount) {
        newErrors.slotSources = `Select a source for each of the ${formData.triggerCount} slots.`;
      } else {
        const invalid = sources.some((s) => !allowed.includes(s));
        if (invalid) newErrors.slotSources = 'Each slot must be Email or Webhook.';
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleTestWebhook = async () => {
    if (!formData.webhookUrl.trim()) {
      setErrors(prev => ({ ...prev, webhookUrl: 'Webhook URL is required for testing' }));
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const result = await onTestWebhook(formData.webhookUrl);
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async (stayOnForm = false) => {
    const validationResult = validateForm();

    if (!validationResult.isValid) {
      const validationErrors = validationResult.errors;
      const errorMessages = Object.values(validationErrors);
      if (errorMessages.length > 0) {
        const webhookError = validationErrors.webhookUrl;
        const message =
          webhookError || errorMessages[0] || 'Please fix the errors in the form before saving';
        setSnackbar({
          open: true,
          message,
          severity: 'error',
        });
      }
      return;
    }

    const isLegacyMulti =
      !!alert?.id && !alert?.alert_kind && !!(alert as AlertConfigType).is_multi_trigger;

    const positionPayload =
      useClassicPositionOptions && formData.positionType === 'Custom'
        ? formData.customPositionType
        : formData.positionType;

    const transformedData: Record<string, unknown> = {
      stock_symbol: formData.stockSymbol.trim().toUpperCase(),
      position_type: positionPayload,
      secret_word: formData.secretWord,
      webhook_url: isMultiKind ? '' : formData.webhookUrl,
      is_active: formData.status === 'active',
      description: (formData.alertName || '').trim() || undefined,
      is_multi_trigger: isMultiKind,
      trigger_count: isMultiKind ? formData.triggerCount : undefined,
      slot_sources: isMultiKind ? formData.slotSources : undefined,
      slot_descriptions: isMultiKind ? formData.slotDescriptions : undefined,
    };

    if (!isMultiKind) {
      if (alert?.id) {
        transformedData.linked_directional_alert_id = alert.linked_directional_alert_id ?? null;
        transformedData.linked_directional_slot_index = alert.linked_directional_slot_index ?? null;
      } else {
        transformedData.linked_directional_alert_id = null;
        transformedData.linked_directional_slot_index = null;
      }
    }
    if (!isLegacyMulti) {
      transformedData.alert_kind = alertKind;
    }
    if (!alert?.id) {
      const payload = formData.customPayload?.trim();
      if (payload) {
        transformedData.custom_payload = payload;
      }
    }

    if (stayOnForm) setSavingContinue(true);
    try {
      const isUpdate = alert?.id;
      const response = isUpdate
        ? await apiService.updateAlertConfiguration(String(alert.id), transformedData, token || undefined)
        : await apiService.createAlertConfiguration(transformedData, token || undefined);

      if (response.success && response.data) {
        const savedConfig = response.data as AlertConfigType;
        const idVal = savedConfig.id != null ? String(savedConfig.id) : undefined;
        console.log('Alert configuration saved successfully:', response.data);
        let snackMessage = stayOnForm
          ? 'Configuration saved. You can generate trigger URLs and manage execution webhooks below.'
          : alert
            ? 'Alert configuration updated successfully'
            : 'Alert configuration created successfully';
        let snackSeverity: 'success' | 'warning' = 'success';

        if (isDirectionalMulti && idVal != null && idVal !== '') {
          setDmtaStatusNonce((n) => n + 1);
        }

        let customPayloadSaveError: string | null = null;
        // Only write custom_payload when the user revealed/edited it, so we do not
        // wipe a masked stored value with null on a routine save.
        const shouldWritePayload = payloadDirty || payloadRevealed || !payloadPresent;
        if (
          shouldWritePayload &&
          (alertKind === 'single' || alertKind === 'directional_multi' || alertKind === 'bidirectional_multi') &&
          idVal != null &&
          idVal !== ''
        ) {
          const aid = parseInt(idVal, 10);
          if (!Number.isNaN(aid)) {
            const payloadTrim = formData.customPayload?.trim() ?? '';
            try {
              const whRes = await apiService.getAlertWebhooks(aid, token || undefined);
              const rows = (whRes.success && whRes.data ? whRes.data : []) as Array<{
                id: number;
                is_primary?: boolean;
              }>;
              const primary = rows.find((w) => w.is_primary) ?? rows[0];
              if (primary?.id != null) {
                await apiService.updateAlertWebhook(
                  primary.id,
                  { custom_payload: payloadTrim === '' ? null : payloadTrim },
                  token || undefined,
                );
              } else if (payloadTrim !== '') {
                customPayloadSaveError =
                  isMultiKind
                    ? 'Custom payload was not saved: add at least one execution webhook under Manage Webhooks, mark one as primary if needed, then save again.'
                    : 'Custom payload was not saved: add a primary execution webhook URL and save again (the payload is stored on that webhook).';
              }
            } catch (e: unknown) {
              customPayloadSaveError =
                e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Unknown error';
              customPayloadSaveError = `Custom payload was not saved: ${customPayloadSaveError}`;
            }
          }
        }
        if (customPayloadSaveError) {
          if (snackSeverity === 'success') snackSeverity = 'warning';
          snackMessage = `${snackMessage} ${customPayloadSaveError}`;
        }

        setSnackbar({
          open: true,
          message: snackMessage,
          severity: snackSeverity,
        });
        const savedAny = savedConfig as AlertConfigType & {
          isMultiTrigger?: boolean;
          triggerCount?: number;
          slotSources?: string[];
        };
        const savedIsMulti = isMultiKind
          ? true
          : !!(savedAny.is_multi_trigger ?? savedAny.isMultiTrigger);
        const savedTc =
          isMultiKind
            ? formData.triggerCount
            : savedAny.trigger_count ?? savedAny.triggerCount;
        const savedSlots =
          isMultiKind && (formData.slotSources?.length ?? 0) > 0
            ? [...(formData.slotSources ?? [])]
            : Array.isArray(savedAny.slot_sources) && savedAny.slot_sources.length > 0
              ? [...savedAny.slot_sources]
              : Array.isArray(savedAny.slotSources) && savedAny.slotSources.length > 0
                ? [...savedAny.slotSources]
                : undefined;
        const savedSlotDescriptions =
          isMultiKind && (formData.slotDescriptions?.length ?? 0) > 0
            ? [...(formData.slotDescriptions ?? [])]
            : Array.isArray(savedAny.slot_descriptions) && (savedAny.slot_descriptions as string[]).length > 0
              ? [...(savedAny.slot_descriptions as string[])]
              : Array.isArray((savedAny as AlertConfigType & { slotDescriptions?: string[] }).slotDescriptions)
                ? [...((savedAny as AlertConfigType & { slotDescriptions?: string[] }).slotDescriptions as string[])]
                : undefined;
        const savedStockSymbol =
          savedConfig.stock_symbol ??
          (savedAny as AlertConfigType & { stockSymbol?: string }).stockSymbol ??
          formData.stockSymbol;
        const savedPositionType =
          savedConfig.position_type ??
          (savedAny as AlertConfigType & { positionType?: string }).positionType ??
          (useClassicPositionOptions && formData.positionType === 'Custom'
            ? formData.customPositionType
            : formData.positionType);
        const savedSecretWord =
          savedConfig.secret_word ??
          (savedAny as AlertConfigType & { secretWord?: string }).secretWord ??
          formData.secretWord;
        const savedWebhookUrl = isMultiKind
          ? ''
          : savedConfig.webhook_url ??
            (savedAny as AlertConfigType & { webhookUrl?: string }).webhookUrl ??
            formData.webhookUrl;
        const savedIsActive =
          savedConfig.is_active ??
          (savedAny as AlertConfigType & { isActive?: boolean }).isActive ??
          (formData.status === 'active');
        const savedCreatedAt =
          savedConfig.created_at ??
          (savedAny as AlertConfigType & { createdAt?: string }).createdAt ??
          alert?.created_at ??
          '';

        if (isBidirectionalMulti) {
          setBmtaStatusNonce((n) => n + 1);
        }

        onSave(
          {
            ...(idVal != null && idVal !== '' ? { id: idVal } : {}),
            user_id: savedConfig.user_id || (alert?.user_id || 1),
            tenant_id: savedConfig.tenant_id || (alert?.tenant_id || 'default'),
            stock_symbol: savedStockSymbol,
            position_type: savedPositionType as PositionType,
            secret_word: savedSecretWord,
            webhook_url: savedWebhookUrl,
            is_active: savedIsActive,
            description: savedConfig.description ?? (formData.alertName?.trim() || undefined),
            created_at: savedCreatedAt,
            alert_kind: (savedConfig as AlertConfigType).alert_kind ?? alertKind,
            is_multi_trigger: savedIsMulti,
            trigger_count: savedIsMulti ? savedTc ?? formData.triggerCount : undefined,
            slot_sources: savedIsMulti
              ? savedSlots ??
                Array.from({ length: formData.triggerCount }, () => 'email')
              : undefined,
            slot_descriptions: savedIsMulti
              ? savedSlotDescriptions ??
                Array.from({ length: formData.triggerCount }, () => '')
              : undefined,
            linked_directional_alert_id: !savedIsMulti
              ? (savedConfig.linked_directional_alert_id ?? null)
              : undefined,
            linked_directional_slot_index: !savedIsMulti
              ? (savedConfig.linked_directional_slot_index ?? null)
              : undefined,
          },
          { stayOnForm }
        );
      } else {
        const errorMessage = response.error || 'Failed to save alert configuration';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        console.error('Failed to save alert configuration:', response);
      }
    } catch (error: any) {
      console.error('Error saving alert configuration:', error);
      let errorMessage = 'An error occurred while saving. Please try again.';
      if (error?.response?.detail) {
        const detail = error.response.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          errorMessage = detail[0].msg;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      if (stayOnForm) setSavingContinue(false);
    }
  };

  const handleGenerateSecretWord = () => {
    const newSecretWord = generateSecretWord();
    setFormData(prev => ({ ...prev, secretWord: newSecretWord }));
  };

  const copyToClipboard = useCallback((text: string): Promise<boolean> => {
    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
    }
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return Promise.resolve(ok);
    } catch {
      document.body.removeChild(textarea);
      return Promise.resolve(false);
    }
  }, []);

  const showCopyResult = useCallback((ok: boolean, successMessage = 'Copied to clipboard') => {
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    setSnackbar({
      open: true,
      message: ok ? successMessage : 'Copy failed',
      severity: ok ? 'success' : 'error'
    });
  }, []);

  const alertId = alert?.id ? (typeof alert.id === 'string' ? parseInt(alert.id, 10) : alert.id) : null;

  const handleBmtaResetSlots = async (slotIndex: number | 'all') => {
    if (!token || alertId == null || Number.isNaN(alertId)) return;
    setBmtaResetting(slotIndex);
    try {
      const body = slotIndex === 'all' ? {} : { slot_index: slotIndex };
      const r = await apiService.resetBidirectionalSlots(alertId, body, token);
      if (r.success) {
        setSnackbar({
          open: true,
          message:
            slotIndex === 'all'
              ? 'All slots reset to neutral.'
              : `Slot ${slotIndex + 1} reset to neutral.`,
          severity: 'success',
        });
        setBmtaStatusNonce((n) => n + 1);
      } else {
        setSnackbar({ open: true, message: r.error || 'Reset failed', severity: 'error' });
      }
    } catch (e: unknown) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Reset failed',
        severity: 'error',
      });
    } finally {
      setBmtaResetting(null);
    }
  };

  /** Custom payload is stored on the primary AlertWebhook (single, DMTA, BMTA). */
  const showCustomPayloadField = true;
  const triggerUrl = triggerToken
    ? `${window.location.origin}/api/v1/incoming-webhook/trigger?token=${encodeURIComponent(triggerToken)}`
    : '';
  const buildSlotTriggerUrl = (slotToken: string) =>
    `${window.location.origin}/api/v1/incoming-webhook/trigger?token=${encodeURIComponent(slotToken)}`;

  // Prefer form state when multi-trigger and form has slot sources so per-slot URL section appears before save
  const slotSources =
    isMultiKind && (formData.slotSources?.length ?? 0) > 0
      ? formData.slotSources!
      : (alert?.slot_sources ?? formData.slotSources ?? []);
  const webhookSlotIndices: number[] = slotSources
    .map((s, i) => (s === 'webhook' ? i : -1))
    .filter((i) => i >= 0);
  const savedSlotSources = alert?.slot_sources ?? [];
  const savedWebhookSlotIndices = savedSlotSources
    .map((s, i) => (s === 'webhook' ? i : -1))
    .filter((i) => i >= 0);
  const slotSourcesMatchSaved =
    savedWebhookSlotIndices.length === webhookSlotIndices.length &&
    savedWebhookSlotIndices.every((s, i) => s === webhookSlotIndices[i]);

  const savedWebhookSlotIndicesKey = savedWebhookSlotIndices.join(',');

  const dmtaSlotActiveRow = useMemo(() => {
    const active = new Set(dmtaTriggerStatus?.active_slots ?? []);
    return Array.from({ length: formData.triggerCount }, (_, i) =>
      active.has(i) ? 'active' : 'inactive',
    );
  }, [dmtaTriggerStatus, formData.triggerCount]);

  const bmtaSlotDirectionsRow = useMemo(() => {
    const raw = bmtaTriggerStatus?.slot_directions ?? bmtaTriggerStatus?.slotDirections;
    const tc = formData.triggerCount;
    if (!Array.isArray(raw) || raw.length !== tc) {
      return Array.from({ length: tc }, () => 'neutral');
    }
    return raw.map((x) => String(x || 'neutral').toLowerCase());
  }, [bmtaTriggerStatus, formData.triggerCount]);

  const loadTriggerToken = useCallback(async () => {
    if (!token || !alertId || Number.isNaN(alertId)) {
      setTriggerToken(null);
      setTriggerLoading(false);
      return;
    }
    setTriggerLoading(true);
    try {
      const response = await apiService.getAlertTriggerToken(alertId, token);
      setTriggerToken(response.data?.token ?? null);
    } catch {
      setTriggerToken(null);
    } finally {
      setTriggerLoading(false);
    }
  }, [alertId, token]);

  const loadSlotTokens = useCallback(async () => {
    if (!token || !alertId || Number.isNaN(alertId) || savedWebhookSlotIndicesKey === '') return;
    const indices = savedWebhookSlotIndicesKey.split(',').map((s) => parseInt(s, 10));
    for (const idx of indices) {
      if (Number.isNaN(idx)) continue;
      setSlotTokenLoading((prev) => ({ ...prev, [idx]: true }));
      try {
        const res = await apiService.getAlertSlotTriggerToken(alertId, idx, token);
        const creds = slotIncomingCredsFromApiPayload(res.data);
        setSlotTriggerTokens((prev) => ({ ...prev, [idx]: creds }));
      } catch {
        setSlotTriggerTokens((prev) => ({ ...prev, [idx]: null }));
      } finally {
        setSlotTokenLoading((prev) => ({ ...prev, [idx]: false }));
      }
    }
  }, [alertId, token, savedWebhookSlotIndicesKey]);

  useEffect(() => {
    if (savedWebhookSlotIndices.length > 0 && alertId != null) void loadSlotTokens();
  }, [alertId, loadSlotTokens, savedWebhookSlotIndices.length]);

  useEffect(() => {
    void loadTriggerToken();
  }, [loadTriggerToken]);

  const handleGenerateTriggerUrl = async () => {
    if (!token || !alertId || Number.isNaN(alertId)) return;
    setTriggerGenerating(true);
    try {
      const response = await apiService.generateAlertTriggerToken(alertId, token);
      setTriggerToken(response.data?.token ?? null);
      setSnackbar({
        open: true,
        message: 'Trigger URL generated',
        severity: 'success'
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Failed to generate trigger URL',
        severity: 'error'
      });
    } finally {
      setTriggerGenerating(false);
    }
  };

  const handleGenerateSlotTriggerUrl = async (slotIndex: number) => {
    if (!token || !alertId || Number.isNaN(alertId)) return;
    setSlotTokenGenerating((prev) => ({ ...prev, [slotIndex]: true }));
    try {
      const response = await apiService.generateAlertSlotTriggerToken(alertId, slotIndex, token);
      const creds = slotIncomingCredsFromApiPayload(response.data);
      setSlotTriggerTokens((prev) => ({ ...prev, [slotIndex]: creds }));
      if (creds) {
        setSnackbar({
          open: true,
          message: `Slot ${slotIndex + 1} trigger URL${isBidirectionalMulti ? 's' : ''} generated`,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: `Server returned no token for slot ${slotIndex + 1}. Try again or check the API response.`,
          severity: 'error'
        });
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail ?? error?.message;
      const saveFirst =
        status === 400 || status === 404 || (typeof detail === 'string' && (detail.includes('webhook slot') || detail.includes('not found')));
      setSnackbar({
        open: true,
        message: saveFirst
          ? 'Save the alert first to generate slot trigger URLs.'
          : (detail || `Failed to generate slot ${slotIndex + 1} trigger URL`),
        severity: 'error'
      });
    } finally {
      setSlotTokenGenerating((prev) => ({ ...prev, [slotIndex]: false }));
    }
  };

  // Format for signal source: #Z# TICKER POSITION_TYPE SECRET_WORD [slot-N] #Z# (uppercase)
  const getBmtaEmailMarkersForSlot = (slotIndex: number): { up: string; down: string } => {
    const positionType =
      useClassicPositionOptions && formData.positionType === 'Custom'
        ? formData.customPositionType
        : formData.positionType;
    const base = `${formData.stockSymbol} ${positionType} ${formData.secretWord}`.toUpperCase();
    const n = slotIndex + 1;
    return {
      up: `#Z# ${base} slot-${n}-up #Z#`,
      down: `#Z# ${base} slot-${n}-down #Z#`,
    };
  };

  type WebhookPayloadExample = {
    title: string;
    hint: string;
    body: string;
    copyText: string;
    accent?: 'success' | 'error' | 'warning';
  };

  const getBmtaWebhookPayloadExamples = (
    slotIndex: number
  ): {
    singleUrl: WebhookPayloadExample[];
    separateUrls: { up: WebhookPayloadExample[]; down: WebhookPayloadExample[] };
  } => {
    const markers = getBmtaEmailMarkersForSlot(slotIndex);
    const upPayload = JSON.stringify({ direction: 'up' }, null, 2);
    const downPayload = JSON.stringify({ direction: 'down' }, null, 2);
    const upWithData = JSON.stringify({ direction: 'up', payload: { note: 'your fields' } }, null, 2);
    const downWithData = JSON.stringify({ direction: 'down', payload: { note: 'your fields' } }, null, 2);
    const downInPayloadOnly = JSON.stringify({ payload: { direction: 'down', note: 'your fields' } }, null, 2);
    const upSignalText = JSON.stringify({ payload: markers.up }, null, 2);
    const downPassthroughOnly = JSON.stringify({ payload: { note: 'your fields' } }, null, 2);
    const deactivate = JSON.stringify({ deactivate: true }, null, 2);
    return {
      singleUrl: [
        {
          title: 'One webhook URL — Up (green)',
          hint:
            'Programs that allow only one webhook (e.g. TradingView): paste your Trigger Up URL below and send this JSON on every alert.',
          body: upPayload,
          copyText: JSON.stringify({ direction: 'up' }),
          accent: 'success',
        },
        {
          title: 'One webhook URL — Down (red)',
          hint: 'Same Trigger Up URL; the server reads direction from JSON (you do not need the Down URL).',
          body: downPayload,
          copyText: JSON.stringify({ direction: 'down' }),
          accent: 'error',
        },
        {
          title: 'One webhook URL — Down (red) + passthrough',
          hint: 'Optional fields are merged into your execution webhook.',
          body: downWithData,
          copyText: JSON.stringify({ direction: 'down', payload: { note: 'your fields' } }),
          accent: 'error',
        },
        {
          title: 'One webhook URL — if direction must live inside payload only',
          hint: 'Use when your platform cannot send a top-level direction field.',
          body: downInPayloadOnly,
          copyText: JSON.stringify({ payload: { direction: 'down', note: 'your fields' } }),
          accent: 'error',
        },
        {
          title: 'One webhook URL — reset slot to neutral',
          hint: 'POST to your Trigger Up URL.',
          body: deactivate,
          copyText: JSON.stringify({ deactivate: true }),
          accent: 'warning',
        },
      ],
      separateUrls: {
        up: [
          {
            title: 'Trigger Up URL only — empty body',
            hint: 'No JSON required when this URL means Up.',
            body: '(empty body)',
            copyText: '',
            accent: 'success',
          },
          {
            title: 'Trigger Up URL — Up with JSON',
            hint: 'Required when your app always sends a body (same URL also accepts direction: down — see One webhook URL above).',
            body: upPayload,
            copyText: JSON.stringify({ direction: 'up' }),
            accent: 'success',
          },
          {
            title: 'Trigger Up URL — Up + passthrough',
            hint: 'Optional payload merged into your execution webhook.',
            body: upWithData,
            copyText: JSON.stringify({ direction: 'up', payload: { note: 'your fields' } }),
            accent: 'success',
          },
          {
            title: 'Trigger Up URL — Signal Source text in payload',
            hint: 'Alternative to direction; must include slot-N-up for this slot.',
            body: upSignalText,
            copyText: JSON.stringify({ payload: markers.up }),
            accent: 'success',
          },
        ],
        down: [
          {
            title: 'Trigger Down URL only — empty body',
            hint: 'Always applies Down; do not send direction: up on this URL.',
            body: '(empty body)',
            copyText: '',
            accent: 'error',
          },
          {
            title: 'Trigger Down URL — passthrough only',
            hint: 'Direction is implied by the Down URL.',
            body: downPassthroughOnly,
            copyText: JSON.stringify({ payload: { note: 'your fields' } }),
            accent: 'error',
          },
        ],
      },
    };
  };

  const getDmtaWebhookPayloadExamples = (): WebhookPayloadExample[] => {
    const activatePayload = JSON.stringify({ payload: { note: 'your fields' } }, null, 2);
    const deactivate = JSON.stringify({ deactivate: true }, null, 2);
    return [
      {
        title: 'Activate slot (green)',
        hint: 'POST to this slot’s Trigger URL below — empty body is enough.',
        body: '(empty body)',
        copyText: '',
        accent: 'success',
      },
      {
        title: 'Activate with passthrough data',
        hint: 'Optional JSON merged into your execution webhook.',
        body: activatePayload,
        copyText: JSON.stringify({ payload: { note: 'your fields' } }),
        accent: 'success',
      },
      {
        title: 'Deactivate / clear slot',
        hint: 'Clears this slot (no longer active).',
        body: deactivate,
        copyText: JSON.stringify({ deactivate: true }),
        accent: 'warning',
      },
    ];
  };

  const renderWebhookPayloadExamples = (examples: WebhookPayloadExample[]) => (
    <Box sx={{ mt: 1 }}>
      {examples.map((ex, idx) => (
        <Box
          key={`${ex.title}-${idx}`}
          sx={{
            mb: 1.5,
            pl: 1.5,
            borderLeft: 3,
            borderColor: ex.accent ? `${ex.accent}.main` : 'divider',
          }}
        >
          <Typography variant="caption" fontWeight={700} color={ex.accent ? `${ex.accent}.main` : 'text.secondary'}>
            {ex.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {ex.hint}
          </Typography>
          <Box
            sx={{
              position: 'relative',
              pr: 4,
              py: 0.75,
              px: 1,
              bgcolor: 'action.hover',
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.8rem',
            }}
          >
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
              {ex.body}
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy payload'}>
              <IconButton
                size="small"
                onClick={() => {
                  void copyToClipboard(ex.copyText || ex.body).then((ok) => showCopyResult(ok, 'Payload copied'));
                }}
                sx={{ position: 'absolute', top: 2, right: 2 }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const getSignalSourceConfigurationForSlot = (slotIndex: number | null): string => {
    const positionType =
      useClassicPositionOptions && formData.positionType === 'Custom'
        ? formData.customPositionType
        : formData.positionType;
    const base = `${formData.stockSymbol} ${positionType} ${formData.secretWord}`.toUpperCase();
    if (slotIndex !== null) {
      return `#Z# ${base} slot-${slotIndex + 1} #Z#`;
    }
    if (!isMultiKind && formData.positionType.toLowerCase() === 'out') {
      const raw = formData.linkedDirectionalSlotIndex.trim();
      if (raw !== '' && !Number.isNaN(Number(raw))) {
        return `#Z# ${base} slot-${Number(raw) + 1} #Z#`;
      }
    }
    return `#Z# ${base} #Z#`;
  };

  const getSignalSourceConfiguration = (): string => {
    return getSignalSourceConfigurationForSlot(isMultiKind ? 0 : null);
  };

  const formTitle = alert
    ? alertKind === 'directional_multi'
      ? 'Edit Directional Trade'
      : 'Edit Two-Way Trade'
    : alertKind === 'directional_multi'
      ? 'Create Directional Trade'
      : 'Create Two-Way Trade';

  const formSubheader =
    alertKind === 'directional_multi'
      ? 'Directional trade (Breakout / Reversal / Sideways/Ranging / Other). Enter runs when every indicator is active. One exit runs when any indicator turns off. Add as many enter and exit destinations as you need under Execution → Manage Webhooks after save.'
      : 'Two-way trade: each slot is neutral, up, or down; consensus Up/Down and Break use separate webhooks (Manage Webhooks).';

  return (
    <Card>
      <CardHeader title={formTitle} subheader={formSubheader} />
      <CardContent>
        <Grid container spacing={3}>
          {/* Stock Symbol */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Stock Symbol"
              value={formData.stockSymbol}
              onChange={(e) => handleInputChange('stockSymbol', e.target.value.toUpperCase())}
              error={!!errors.stockSymbol}
              helperText={errors.stockSymbol || 'Enter the stock ticker symbol (e.g., AAPL)'}
              placeholder="AAPL"
              inputProps={{ maxLength: 5 }}
            />
          </Grid>

          {/* Alert Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Alert Name"
              value={formData.alertName}
              onChange={(e) => handleInputChange('alertName', e.target.value)}
              helperText="Optional unique name for this alert (shown in the Alerts list)"
              placeholder="e.g. AAPL breakout watch"
            />
          </Grid>

          {/* Market Condition */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.positionType}>
              <InputLabel id="br-ac-position-type-label">Market Condition</InputLabel>
              <Select
                labelId="br-ac-position-type-label"
                value={formData.positionType}
                onChange={(e) => handleInputChange('positionType', e.target.value)}
                label="Market Condition"
              >
                {useClassicPositionOptions
                  ? LEGACY_MULTI_POSITION_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))
                  : DIRECTIONAL_POSITION_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Custom Position Type Input — legacy multi rows only */}
          {useClassicPositionOptions && formData.positionType === 'Custom' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Position Type Description"
                value={formData.customPositionType}
                onChange={(e) => handleInputChange('customPositionType', e.target.value)}
                error={!!errors.customPositionType}
                helperText={errors.customPositionType || 'Enter your custom position type description'}
                placeholder="e.g., Custom Iron Condor with specific strikes"
                multiline
                rows={2}
              />
            </Grid>
          )}

          {/* Secret Word */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Secret Word"
              value={formData.secretWord}
              onChange={(e) => handleInputChange('secretWord', e.target.value)}
              error={!!errors.secretWord}
              helperText={errors.secretWord || 'Unique identifier for this alert'}
              placeholder="alpha123"
              InputProps={{
                endAdornment: (
                  <Tooltip title="Generate random secret word">
                    <IconButton onClick={handleGenerateSecretWord} edge="end">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          </Grid>

          {/* Custom payload — high on the form so Edit DMTA users see it (stored on primary AlertWebhook). */}
          {showCustomPayloadField && (
            <Grid item xs={12}>
              {alert?.id && payloadPresent && !payloadRevealed && !payloadDirty && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    Stored payload is masked. Re-authenticate to view or edit it.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setReauthOpen(true)}
                    disabled={primaryWebhookId == null}
                  >
                    Reveal
                  </Button>
                </Box>
              )}
              <TextField
                fullWidth
                label="JSON payload or broker information (optional)"
                value={formData.customPayload}
                onChange={(e) => {
                  setPayloadDirty(true);
                  handleInputChange('customPayload', e.target.value);
                }}
                error={!!errors.customPayload}
                helperText={
                  errors.customPayload ||
                  'Encrypted at rest and masked on display. Reveal requires re-authentication. Max 8KB; valid JSON if provided.'
                }
                placeholder={
                  alert?.id && payloadPresent && !payloadRevealed
                    ? 'Reveal to edit, or type a new payload to replace'
                    : '{"key": "value"}'
                }
                multiline
                minRows={2}
                maxRows={6}
                inputProps={{ maxLength: CUSTOM_PAYLOAD_MAX_BYTES }}
              />
            </Grid>
          )}

          {/* Webhook URL (single-trigger: here; multi-trigger: after slot sources) */}
          {!isMultiKind && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Webhook URL"
                value={formData.webhookUrl}
                onChange={(e) => handleInputChange('webhookUrl', e.target.value)}
                error={!!errors.webhookUrl}
                helperText={errors.webhookUrl || 'Your trade execution webhook URL'}
                placeholder="https://your-trade-execution-url.com/webhook/..."
              />
            </Grid>
          )}

          {/* Directional / bi-directional multi-trigger (no checkbox — type is chosen from create flow) */}
          {isMultiKind && (
            <Grid item xs={12}>
              <Box sx={{ pt: 1, pb: 1 }}>
                <Box sx={{ pl: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {isDirectionalMulti
                      ? `Your plan: up to ${maxTriggerCount} slots (use 1 for a single condition). All slots must activate before the completion webhook runs.`
                      : `Your plan: up to ${maxTriggerCount} slots. Each slot is neutral, up, or down. Full agreement fires Up or Down webhooks; losing agreement fires Break (see Manage Webhooks).`}
                  </Typography>
                  <FormControl sx={{ minWidth: 120, mr: 2, mb: 2 }} size="small" error={!!errors.triggerCount}>
                    <InputLabel id="br-ac-trigger-count-label">Number of Indicators</InputLabel>
                    <Select
                      labelId="br-ac-trigger-count-label"
                      value={formData.triggerCount}
                      label="Number of Indicators"
                      onChange={(e) => handleTriggerCountChange(Number(e.target.value))}
                    >
                      {triggerCountOptions.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Slot sources and labels
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Choose Email or Webhook per slot. Use the label to remember what each slot represents (e.g.
                    indicator name).
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {(formData.slotSources ?? []).map((source, i) => (
                      <Box
                        key={i}
                        sx={{
                          p: 1.5,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          maxWidth: 560,
                        }}
                      >
                        <FormControl fullWidth size="small" error={!!errors.slotSources} sx={{ mb: 1 }}>
                          <InputLabel id={`br-ac-slot-src-${i}-label`}>Slot {i + 1} source</InputLabel>
                          <Select
                            labelId={`br-ac-slot-src-${i}-label`}
                            value={source}
                            label={`Slot ${i + 1} source`}
                            onChange={(e) => handleSlotSourceChange(i, e.target.value)}
                          >
                            {slotSourceOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          size="small"
                          label={`Slot ${i + 1} description (optional)`}
                          value={(formData.slotDescriptions ?? [])[i] ?? ''}
                          onChange={(e) => handleSlotDescriptionChange(i, e.target.value)}
                          placeholder="e.g. RSI cross, MACD histogram"
                          disabled={savingContinue || loading}
                        />
                      </Box>
                    ))}
                  </Box>
                  {errors.slotSources && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {errors.slotSources}
                    </Typography>
                  )}

                  {isDirectionalMulti && alert?.id && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Runtime slot state
                      </Typography>
                      {dmtaTriggerLoading ? (
                        <Typography variant="body2" color="text.secondary">
                          Loading slot state…
                        </Typography>
                      ) : (
                        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" sx={{ mb: 1 }}>
                          {dmtaSlotActiveRow.map((state, i) => (
                            <Tooltip
                              key={i}
                              title={
                                state === 'active'
                                  ? `Slot ${i + 1}: triggered (green)`
                                  : `Slot ${i + 1}: waiting (empty)`
                              }
                            >
                              <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                <Box
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    bgcolor: state === 'active' ? '#2e7d32' : 'transparent',
                                    border:
                                      state === 'active'
                                        ? '2px solid transparent'
                                        : '2px solid rgba(0,0,0,0.26)',
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {i + 1}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}

                  {isBidirectionalMulti && alert?.id && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Runtime slot state
                      </Typography>
                      {bmtaTriggerLoading ? (
                        <Typography variant="body2" color="text.secondary">
                          Loading slot state…
                        </Typography>
                      ) : (
                        <>
                          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" sx={{ mb: 1 }}>
                            {bmtaSlotDirectionsRow.map((dir, i) => (
                              <Chip
                                key={i}
                                label={`Slot ${i + 1}${(formData.slotDescriptions ?? [])[i]?.trim() ? ` (${(formData.slotDescriptions ?? [])[i].trim()})` : ''}: ${dir}`}
                                size="small"
                                color={
                                  dir === 'neutral' ? 'default' : dir === 'up' ? 'success' : 'error'
                                }
                                variant={dir === 'neutral' ? 'outlined' : 'filled'}
                              />
                            ))}
                          </Box>
                          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={bmtaResetting !== null}
                              onClick={() => void handleBmtaResetSlots('all')}
                            >
                              {bmtaResetting === 'all' ? 'Resetting…' : 'Reset all to neutral'}
                            </Button>
                            {bmtaSlotDirectionsRow.map((_, i) => (
                              <Button
                                key={i}
                                size="small"
                                variant="text"
                                disabled={bmtaResetting !== null}
                                onClick={() => void handleBmtaResetSlots(i)}
                              >
                                {bmtaResetting === i ? '…' : `Reset slot ${i + 1}`}
                              </Button>
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  )}

                  {isDirectionalMulti && (
                    <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                      Add <strong>enter</strong> and <strong>exit</strong> destinations under{' '}
                      <strong>Execution → Manage Webhooks</strong> after save. Exit is one condition: it runs when{' '}
                      <strong>any</strong> indicator turns off, and it can send to more than one destination.
                      Per-indicator trigger URLs turn a slot <strong>green</strong> (active) or <strong>empty</strong>{' '}
                      (clear with <code>deactivate: true</code> or <code>slot_active: false</code> in the JSON payload).
                    </Alert>
                  )}
                  {isBidirectionalMulti && (
                    <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                      Add <strong>Up</strong>, <strong>Down</strong>, and <strong>Break</strong> webhooks under{' '}
                      <strong>Execution → Manage Webhooks</strong>. This is not the same as a directional multi-trigger{' '}
                      <strong>Out</strong> exit row.
                    </Alert>
                  )}

                  <Box>
                    <Button
                      variant="contained"
                      color="primary"
                     startIcon={<SaveIcon />}
                      disabled={savingContinue || loading}
                      onClick={() => void handleSave(true)}
                    >
                      {savingContinue ? 'Saving…' : 'Save configuration'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Saves to the database without leaving this screen so you can generate trigger URLs and finish setup below.
                    </Typography>
                  </Box>

                </Box>
              </Box>
            </Grid>
          )}

          {/* Per-alert or per-slot trigger URL(s) */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Trigger This Alert From An External App
              </Typography>
              {!alertId ? (
                <Alert severity="info">
                  Save this alert first to generate its unique trigger URL. Once saved, open Edit Alert to copy or rotate the URL.
                </Alert>
              ) : webhookSlotIndices.length > 0 ? (
                <>
                  {!slotSourcesMatchSaved && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Save the alert to generate or load slot trigger URLs.
                    </Alert>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {isBidirectionalMulti
                      ? 'Each webhook slot gets two trigger URLs: one sets that slot to Up, the other to Down. You may POST JSON with a payload object to merge into the execution webhook when this slot fires (same shape as other incoming triggers). Use deactivate: true on either URL to reset that slot to neutral.'
                      : 'Each webhook slot has its own URL. Use the slot-specific URL so that slot is activated (no request body required).'}
                  </Typography>
                  {webhookSlotIndices.map((slotIndex) => {
                    const isSavedSlot = savedWebhookSlotIndices.includes(slotIndex);
                    const creds = slotTriggerTokens[slotIndex] ?? null;
                    const slotLoading = slotTokenLoading[slotIndex];
                    const generating = slotTokenGenerating[slotIndex];
                    const hasPair = !!(creds?.tokenUp && creds?.tokenDown);
                    const legacyToken = creds?.token ?? null;
                    const urlUp = creds?.tokenUp ? buildSlotTriggerUrl(creds.tokenUp) : '';
                    const urlDown = creds?.tokenDown ? buildSlotTriggerUrl(creds.tokenDown) : '';
                    const urlLegacy = legacyToken ? buildSlotTriggerUrl(legacyToken) : '';
                    const slotLabel = (formData.slotDescriptions ?? [])[slotIndex]?.trim();
                    return (
                      <Box key={slotIndex} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Slot {slotIndex + 1} (Webhook)
                          {slotLabel ? ` — ${slotLabel}` : ''}
                        </Typography>
                        {!isSavedSlot ? (
                          <Typography variant="body2" color="text.secondary">
                            Save the alert first to generate this slot&apos;s URL.
                          </Typography>
                        ) : slotLoading ? (
                          <Typography variant="body2" color="text.secondary">Loading…</Typography>
                        ) : hasPair ? (
                          <Box display="flex" flexDirection="column" gap={1.5}>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Trigger Up (unique URL)
                              </Typography>
                              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={urlUp}
                                  InputProps={{ readOnly: true }}
                                  sx={{ flex: 1, minWidth: 280 }}
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    void copyToClipboard(urlUp).then((ok) => showCopyResult(ok, 'Up trigger URL copied'))
                                  }
                                >
                                  Copy
                                </Button>
                              </Box>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                Trigger Down (unique URL)
                              </Typography>
                              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                <TextField
                                  fullWidth
                                  size="small"
                                  value={urlDown}
                                  InputProps={{ readOnly: true }}
                                  sx={{ flex: 1, minWidth: 280 }}
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    void copyToClipboard(urlDown).then((ok) =>
                                      showCopyResult(ok, 'Down trigger URL copied'),
                                    )
                                  }
                                >
                                  Copy
                                </Button>
                              </Box>
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleGenerateSlotTriggerUrl(slotIndex)}
                              disabled={generating}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              {generating ? 'Generating…' : 'Rotate Up & Down URLs'}
                            </Button>
                          </Box>
                        ) : legacyToken ? (
                          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            <TextField
                              fullWidth
                              size="small"
                              value={urlLegacy}
                              InputProps={{ readOnly: true }}
                              sx={{ flex: 1, minWidth: 280 }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                void copyToClipboard(urlLegacy).then((ok) => showCopyResult(ok, 'Trigger URL copied'))
                              }
                            >
                              Copy URL
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleGenerateSlotTriggerUrl(slotIndex)}
                              disabled={generating}
                            >
                              {generating ? 'Generating…' : 'Rotate URL'}
                            </Button>
                          </Box>
                        ) : (
                          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                              No trigger URLs for this slot yet.
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleGenerateSlotTriggerUrl(slotIndex)}
                              disabled={generating}
                            >
                              {generating ? 'Generating…' : isBidirectionalMulti ? 'Generate Up & Down URLs' : 'Generate URL'}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                  {isBidirectionalMulti && webhookSlotIndices.length > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <strong>One webhook only?</strong> Use the <strong>Trigger Up</strong> URL with JSON{' '}
                      <code>{'{"direction":"up"}'}</code> or <code>{'{"direction":"down"}'}</code> — see{' '}
                      <strong>Signal Source</strong> below. Two-URL setups can use separate Up/Down URLs instead.
                    </Alert>
                  )}
                </>
              ) : triggerLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    Loading trigger URL...
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {isBidirectionalMulti
                      ? 'All slots are Email only. Add a Webhook slot above for per-slot Up/Down trigger URLs, or use this alert-level URL only with POST and a JSON direction field (legacy).'
                      : 'Use this unique URL from any tool that can open a URL. No request body required.'}
                  </Typography>
                  {triggerToken ? (
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <TextField
                        fullWidth
                        size="small"
                        value={triggerUrl}
                        InputProps={{ readOnly: true }}
                        sx={{ flex: 1, minWidth: 280 }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => {
                          void copyToClipboard(triggerUrl).then((ok) => showCopyResult(ok, 'Trigger URL copied'));
                        }}
                      >
                        Copy URL
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleGenerateTriggerUrl}
                        disabled={triggerGenerating}
                      >
                        {triggerGenerating ? 'Generating...' : 'Rotate URL'}
                      </Button>
                    </Box>
                  ) : (
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" color="text.secondary">
                        No trigger URL has been generated for this alert yet.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleGenerateTriggerUrl}
                        disabled={triggerGenerating}
                      >
                        {triggerGenerating ? 'Generating...' : 'Generate URL'}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>

          {/* Signal Source (Copy to Signal Source) – one per slot when multi-trigger, with #Z# and slot-1/slot-2... */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Signal Source (Copy to Signal Source)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isBidirectionalMulti
                ? 'Email: copy the Up and Down marker lines per slot. Webhook slots: if your platform allows only one URL, use Trigger Up only and set direction to up or down in JSON (examples below). Otherwise use separate Trigger Up / Down URLs.'
                : 'Copy each string into your signal source platform. For webhook slots, use the POST body examples below with that slot’s Trigger URL.'}
            </Typography>
            {!isMultiKind &&
              formData.positionType.toLowerCase() === 'out' &&
              ambiguousOutCount > 1 &&
              formData.linkedDirectionalSlotIndex.trim() === '' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Multiple Out alerts share this symbol and secret. Add <strong>slot-N</strong> (1-based) to your
                  platform signal so the correct alert is chosen.
                </Alert>
              )}
            {isMultiKind && (formData.slotSources?.length ?? 0) > 0 ? (
              (formData.slotSources ?? []).map((_source, i) => {
                const slotSource = String((formData.slotSources ?? [])[i] ?? 'email').toLowerCase();
                const isWebhookSlot = slotSource === 'webhook';
                const markers = getBmtaEmailMarkersForSlot(i);
                const copyText = isBidirectionalMulti
                  ? `Up:\n${markers.up}\n\nDown:\n${markers.down}`
                  : getSignalSourceConfigurationForSlot(i);
                const bmtaWebhook = isBidirectionalMulti && isWebhookSlot ? getBmtaWebhookPayloadExamples(i) : null;
                const dmtaWebhook = isDirectionalMulti && isWebhookSlot ? getDmtaWebhookPayloadExamples() : null;
                return (
                  <Box
                    key={i}
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      position: 'relative',
                      color: 'text.primary',
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Slot {i + 1} ({(formData.slotSources ?? [])[i] ?? 'email'})
                    </Typography>
                    {isWebhookSlot ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontFamily: 'inherit' }}>
                          Generate Trigger URLs above for slot {i + 1}. If your app allows <strong>only one</strong>{' '}
                          webhook, use <strong>Trigger Up</strong> for every signal and set{' '}
                          <code>direction</code> to <code>up</code> or <code>down</code> in JSON.
                        </Typography>
                        {bmtaWebhook ? (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                              One webhook URL (Trigger Up only)
                            </Typography>
                            {renderWebhookPayloadExamples(bmtaWebhook.singleUrl)}
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                              Optional: separate Trigger Up and Trigger Down URLs
                            </Typography>
                            <Typography variant="caption" fontWeight={600} color="success.main" display="block">
                              Trigger Up URL
                            </Typography>
                            {renderWebhookPayloadExamples(bmtaWebhook.separateUrls.up)}
                            <Typography variant="caption" fontWeight={600} color="error.main" display="block" sx={{ mt: 1 }}>
                              Trigger Down URL
                            </Typography>
                            {renderWebhookPayloadExamples(bmtaWebhook.separateUrls.down)}
                          </>
                        ) : dmtaWebhook ? (
                          renderWebhookPayloadExamples(dmtaWebhook)
                        ) : null}
                      </>
                    ) : isBidirectionalMulti ? (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Up signal (email / text)
                        </Typography>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{markers.up}</pre>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, mb: 0.5 }}>
                          Down signal (email / text)
                        </Typography>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{markers.down}</pre>
                        <Tooltip title={copied ? 'Copied!' : 'Copy markers'}>
                          <IconButton
                            onClick={() => {
                              void copyToClipboard(copyText).then((ok) => showCopyResult(ok));
                            }}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {getSignalSourceConfigurationForSlot(i)}
                        </pre>
                        <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                          <IconButton
                            onClick={() => {
                              void copyToClipboard(copyText).then((ok) => showCopyResult(ok));
                            }}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                );
              })
            ) : (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  position: 'relative',
                  color: 'text.primary',
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {getSignalSourceConfiguration()}
                </pre>
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    onClick={() => {
                      void copyToClipboard(getSignalSourceConfiguration()).then((ok) => showCopyResult(ok));
                    }}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Grid>

          {/* Execution: manage additional webhooks + test (same as Edit Alert; available after save when alert has id) */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Execution
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isBidirectionalMulti
                ? 'Add Up, Down, and Break execution targets under Manage Webhooks. Delivery testing is available there once URLs exist.'
                : isDirectionalMulti
                  ? 'Add enter and exit destinations under Manage Webhooks. Exit runs when any indicator turns off, and both enter and exit can have more than one destination. Delivery testing is available there once URLs exist.'
                  : 'Configure multiple webhook URLs for this alert based on your subscription tier. Test delivery to your primary or configured URLs.'}
            </Typography>
            {alertId != null && !Number.isNaN(alertId) && (
              <Box
                sx={{
                  p: 2,
                  mb: 2,
                  bgcolor: 'background.default',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Multiple Webhooks
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={() => {
                      if (alertId == null || Number.isNaN(alertId)) return;
                      setWebhookManagerContext({
                        alertId,
                        alertName:
                          formData.alertName?.trim() ||
                          `${formData.stockSymbol} - ${
                            useClassicPositionOptions && formData.positionType === 'Custom'
                              ? formData.customPositionType
                              : formData.positionType
                          }`,
                      });
                    }}
                  >
                    Manage Webhooks
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {isMultiKind
                    ? 'Open Manage Webhooks to add execution URLs, run delivery tests, and set custom payload on the primary row.'
                    : 'Configure multiple webhook URLs for this alert based on your subscription tier.'}
                </Typography>
              </Box>
            )}
            {!isMultiKind && (
              <>
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
                  <CustomButton
                    variant="secondary"
                    onClick={handleTestWebhook}
                    loading={testing}
                    startIcon={<TestIcon />}
                    disabled={!formData.webhookUrl.trim()}
                  >
                    Test Webhook
                  </CustomButton>
                  {testResult && (
                    <AlertStatus
                      status={testResult.success ? 'success' : 'error'}
                      executionTime={testResult.executionTime}
                      label={testResult.success ? 'Test Successful' : 'Test Failed'}
                    />
                  )}
                </Box>
                {testResult && !testResult.success && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Webhook test failed. Please check your URL and try again.
                  </Alert>
                )}
              </>
            )}
            {alertId == null && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Save the alert configuration first to manage additional webhooks for execution.
              </Alert>
            )}
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <CustomButton
                variant="secondary"
                onClick={onCancel}
                startIcon={<CancelIcon />}
                disabled={loading}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="primary"
                onClick={() => void handleSave(false)}
                loading={loading}
                startIcon={<SaveIcon />}
                disabled={
                  Object.keys(errors).length > 0 ||
                  !formData.stockSymbol ||
                  !formData.secretWord ||
                  (!isMultiKind && !formData.webhookUrl.trim())
                }
              >
                {alert ? 'Update Alert' : 'Create Alert'}
              </CustomButton>
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      {webhookManagerContext != null && (
        <MultipleWebhooksManager
          open
          onClose={() => {
            setWebhookManagerContext(null);
            if (webhookManagerContext.alertId === alertId && alertId != null && !Number.isNaN(alertId)) {
              setPrimaryWebhookMetaNonce((n) => n + 1);
            }
            if (
              alertKind === 'bidirectional_multi' &&
              webhookManagerContext.alertId === alertId &&
              alertId != null &&
              !Number.isNaN(alertId)
            ) {
              setBmtaStatusNonce((n) => n + 1);
            }
            if (
              alertKind === 'directional_multi' &&
              webhookManagerContext.alertId === alertId &&
              alertId != null &&
              !Number.isNaN(alertId)
            ) {
              setDmtaStatusNonce((n) => n + 1);
            }
          }}
          alertId={webhookManagerContext.alertId}
          alertName={webhookManagerContext.alertName}
          alertKind={alertKind}
          triggerCount={isDirectionalMulti ? formData.triggerCount : undefined}
        />
      )}

      <ReauthDialog
        open={reauthOpen}
        onClose={() => setReauthOpen(false)}
        onSuccess={async (stepUpToken) => {
          if (primaryWebhookId == null) return;
          try {
            const res = await apiService.revealAlertWebhookPayload(
              primaryWebhookId,
              stepUpToken,
              token || undefined
            );
            if (res.success) {
              setFormData((prev) => ({
                ...prev,
                customPayload: res.data?.custom_payload ?? '',
              }));
              setPayloadRevealed(true);
              setPayloadDirty(false);
            } else {
              setSnackbar({
                open: true,
                message: res.error || 'Failed to reveal payload',
                severity: 'error',
              });
            }
          } catch (e: unknown) {
            setSnackbar({
              open: true,
              message: e instanceof Error ? e.message : 'Failed to reveal payload',
              severity: 'error',
            });
          }
        }}
        description="Re-enter your account password to view this transmitted payload."
      />

      {/* Snackbar for toast notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AlertConfiguration;
